import LegalPage, { H2, P, UL } from "@/components/LegalPage";
import { BUSINESS_ADDRESS, PHONE_DISPLAY } from "@/lib/whatsapp";
import { SITE_URL, SITE_HOST } from "@/lib/site";

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      effectiveDate="08 May 2026"
      path="/privacy"
      description="How Weight Loss Gurukul collects, uses, stores, and protects your personal data — for clients in India, the EU, the UK, the US and worldwide."
    >
      <P>
        This Privacy Policy explains how <strong>Weight Loss Gurukul</strong>{" "}
        ("we", "us", "our") collects, uses, discloses and safeguards information
        when you visit <a href={SITE_URL} className="text-forest underline">{SITE_HOST}</a>{" "}
        or interact with our online yoga programs and services (the "Services").
        By using the Services you agree to this Policy.
      </P>

      <H2>1. Who we are</H2>
      <P>
        Weight Loss Gurukul is an online yoga and wellness practice founded by
        Dhruva Lingappa, operating from {BUSINESS_ADDRESS.full}. We serve
        clients in India and worldwide.
      </P>

      <H2>2. Information we collect</H2>
      <UL>
        <li>
          <strong>Information you give us</strong>: name, email, phone /
          WhatsApp number, country, goal, and any messages you send us via
          forms, email, WhatsApp or during a consultation.
        </li>
        <li>
          <strong>Health and lifestyle information</strong> you voluntarily
          share so we can personalise your program (e.g. weight, medical
          conditions, dietary preferences). You decide what to share.
        </li>
        <li>
          <strong>Payment information</strong>: handled directly by our payment
          processors. We do not store your full card or bank details on our
          servers.
        </li>
        <li>
          <strong>Automatic information</strong>: device, browser, IP address,
          referring URL, pages visited, time on page, and similar telemetry —
          collected via cookies and analytics tools.
        </li>
      </UL>

      <H2>3. How we use information</H2>
      <UL>
        <li>To respond to your enquiries and schedule your free consultation.</li>
        <li>To deliver, personalise and improve our yoga programs.</li>
        <li>To send program-related updates, schedules, recordings and renewals.</li>
        <li>
          To send marketing emails about new programs, blog posts and offers —
          only if you have opted in. You can unsubscribe at any time.
        </li>
        <li>
          To measure and improve our website and content using Google Analytics
          (GA4) and similar tools.
        </li>
        <li>To prevent fraud, enforce our terms and comply with the law.</li>
      </UL>

      <H2>4. Lawful basis (EU/UK users)</H2>
      <P>
        We rely on your <em>consent</em> for marketing communications and
        non-essential cookies, on the <em>performance of a contract</em> when
        you enrol in a program, on our <em>legitimate interest</em> to operate
        and improve our Services, and on <em>legal obligation</em> to keep
        certain records (e.g. tax). For India, our processing follows the
        Digital Personal Data Protection Act, 2023 (DPDP Act).
      </P>

      <H2>5. Sharing your information</H2>
      <P>
        We do not sell your personal data. We share it only with trusted
        processors who help us run the Services:
      </P>
      <UL>
        <li><strong>Resend</strong> — for transactional and notification email.</li>
        <li><strong>Google Analytics 4</strong> — for anonymised website analytics.</li>
        <li><strong>WhatsApp / Meta</strong> — when you choose to message us via the WhatsApp button.</li>
        <li><strong>Payment processors</strong> — to process program fees, when applicable.</li>
        <li><strong>Cloud hosting providers</strong> — to host the website and our database.</li>
        <li><strong>Authorities</strong> — only if required by law, court order, or to protect rights and safety.</li>
      </UL>

      <H2>6. International transfers</H2>
      <P>
        Some of our service providers operate outside India (e.g. in the United
        States or the European Union). Where required, we put appropriate
        safeguards in place — such as Standard Contractual Clauses — and rely
        on your consent for such transfers.
      </P>

      <H2>7. Cookies and analytics</H2>
      <P>
        We use cookies and similar technologies for essential site functions
        and for Google Analytics (GA4). You can disable non-essential cookies
        in your browser, and opt out of GA tracking using the{" "}
        <a
          href="https://tools.google.com/dlpage/gaoptout"
          target="_blank"
          rel="noopener noreferrer"
          className="text-forest underline"
        >
          Google Analytics opt-out add-on
        </a>
        .
      </P>

      <H2>8. Data retention</H2>
      <P>
        We keep lead and enquiry data for up to 24 months from your last
        interaction. Active client records are kept while your program is
        ongoing and for up to 7 years thereafter to meet tax and accounting
        rules. You can request earlier deletion at any time (see Section 10).
      </P>

      <H2>9. Security</H2>
      <P>
        We use reasonable administrative, technical and physical safeguards —
        including encrypted transport (HTTPS), access controls and reputable
        cloud providers — to protect your information. No method of internet
        transmission is 100% secure; please share only what you are comfortable
        with.
      </P>

      <H2>10. Your rights</H2>
      <P>
        Depending on where you live, you may have the right to access,
        correct, update, port, restrict, object to or delete your personal
        data, and to withdraw consent at any time. EU/UK users can lodge a
        complaint with their data protection authority. California residents
        have rights under the CCPA/CPRA. Indian users have rights under the
        DPDP Act.
      </P>
      <P>
        To exercise any right, email us at{" "}
        <a className="text-forest underline" href="mailto:hello@weightlossgurukul.com">
          hello@weightlossgurukul.com
        </a>{" "}
        from your registered email. We respond within 30 days.
      </P>

      <H2>11. Children</H2>
      <P>
        Our Services are intended for users aged 18 and above. We do not
        knowingly collect personal data from children under 18. If you believe
        a child has given us data, please contact us and we will delete it.
      </P>

      <H2>12. Changes to this Policy</H2>
      <P>
        We may update this Policy from time to time. The "Effective date"
        above tells you when it was last revised. Material changes will be
        announced on this page or via email.
      </P>

      <H2>13. Contact</H2>
      <P>
        Weight Loss Gurukul<br />
        {BUSINESS_ADDRESS.line1}, {BUSINESS_ADDRESS.line2},{" "}
        {BUSINESS_ADDRESS.line3}, {BUSINESS_ADDRESS.line4}<br />
        Email:{" "}
        <a className="text-forest underline" href="mailto:hello@weightlossgurukul.com">
          hello@weightlossgurukul.com
        </a>{" "}
        · Phone: {PHONE_DISPLAY}
      </P>
    </LegalPage>
  );
}
